import React from 'react';
import { useMutation } from '@apollo/react-hooks';
import { Formik, Form, FormikValues } from 'formik';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Button } from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import PersonRoundedIcon from '@material-ui/icons/PersonRounded';
import WorkIcon from '@material-ui/icons/Work';

import { FormikInput } from 'components';
import useGlobalStyles from 'hooks/useGlobalStyles';
import { creatorValidationSchema } from './ValidationSchema';
import { UPDATE_SHOW_CREATOR } from 'graphql/show';
import { filterUnchangedValues } from 'utils/form';
import { ShowCreator } from 'types';

interface Props {
  itunesId: string;
  creator?: ShowCreator;
  isEditing: boolean;
  isOpen: boolean;
  onClose: () => void;
}

const emptyCreator = {
  name: '',
  role: '',
  link: '',
};

const CreatorsDialog: React.FC<Props> = (props) => {
  const { creator, isEditing, isOpen, itunesId, onClose } = props;
  const globalClasses = useGlobalStyles();

  // Local State
  const initialValues = creator || emptyCreator;
  const initialErrors = isEditing ? {} : { name: '', role: '' };
  const [updateCreator] = useMutation(UPDATE_SHOW_CREATOR);

  const handleFormSubmit = async (values: FormikValues) => {
    const updates = filterUnchangedValues(initialValues, values);
    const input = { itunesId, creator: { ...updates, id: creator?.id || undefined } };

    if (Object.keys(updates).length === 0) return;
    try {
      await updateCreator({ variables: { input } });
    } catch (e) {
      console.error(e.message);
    } finally {
      onClose();
    }
  };

  const handleDelete = async () => {
    const input = { itunesId, creator: { id: creator!.id }, isDelete: true };
    try {
      await updateCreator({ variables: { input } });
    } catch (e) {
      console.error(e.message);
    } finally {
      onClose();
    }
  };

  const renderForm = () => (
    <Formik
      initialValues={initialValues}
      initialErrors={initialErrors}
      validationSchema={creatorValidationSchema}
      onSubmit={handleFormSubmit}
      validateOnChange
    >
      {({ isValid, values }) => (
        <Form>
          <DialogContent>
            <Grid item>
              <FormikInput
                title="Name"
                name="name"
                placeholder="Full Name"
                endAdornment={<PersonRoundedIcon />}
              />
            </Grid>
            <Grid item>
              <FormikInput
                title="Role"
                name="role"
                placeholder="Host"
                endAdornment={<WorkIcon />}
              />
            </Grid>
            <Grid item>
              <FormikInput
                title="Link"
                name="link"
                placeholder="Full Name"
                endAdornment={<LinkIcon />}
              />
            </Grid>
          </DialogContent>
          <DialogActions style={{ justifyContent: 'flex-start' }}>
            {isEditing && (
              <Button color="primary" onClick={() => handleDelete()}>
                Delete
              </Button>
            )}
            <div className={globalClasses.actionButtonGroup}>
              <Button color="primary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={!isValid}>
                Update
              </Button>
            </div>
          </DialogActions>
        </Form>
      )}
    </Formik>
  );

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={isOpen}
      onClose={onClose}
      aria-labelledby="creators-dialog-title"
    >
      <DialogTitle id="creators-dialog-title">Creator Details</DialogTitle>
      {renderForm()}
    </Dialog>
  );
};

export default CreatorsDialog;
